<script setup lang="ts">
import { ref, onMounted, computed, inject, watch } from "vue";
import type { Ref } from "vue";
import { APIURL } from "@/defaults";

import type { StockDataObject } from "@/interfaces/interfaces";

import Fuse from "fuse.js";
import router from "@/router";

const active: Ref<boolean> = ref(false);
const input: Ref<string> = ref("");
const inputField: Ref<HTMLInputElement | null> = ref(null);
const selectedIndex: Ref<number> = ref(0);

interface SearchResult {
    id: string;
    name: string;
    ticker: string;
    action: () => void;
    fund_205310: number | null;
    fund_205410: number | null;
    fund_206910: number | null;
    fund_207610: number | null;
}

const results: Ref<SearchResult[]> = ref([]);

const stocks = inject('stocks') as Ref<StockDataObject | null>


onMounted(() => {
    document.addEventListener("keydown", (e: KeyboardEvent) => {
        if (e.code === "KeyK" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            active.value = !active.value;
            if (active.value) {
                setTimeout(() => {
                    inputField.value?.focus();
                }, 1);
            }
        }
        if (active.value) {
            if (e.code === "Escape") {
                active.value = false;
            }
            if (e.code === "Enter") {
                // perform selected event action
                searchResults.value[selectedIndex.value].action();
                active.value = false;
                input.value = "";
            }
            if (e.code === "ArrowUp") {
                selectedIndex.value = Math.max(0, selectedIndex.value - 1);
            }
            if (e.code === "ArrowDown") {
                selectedIndex.value = Math.min(searchResults.value.length - 1, selectedIndex.value + 1);
            }
        }
    });
});

const fuse = new Fuse(results.value, {
    keys: ["ticker", "name"],
    threshold: 0.2,
});

watch(stocks, (newStocks) => {
    if (!newStocks) return;

    results.value = Object.values(newStocks)
        .filter(s => s.active !== false) // Only include stocks that don't have active explicitly set to false
        .filter(s => s.isin.indexOf('_') === -1)
        .map((s) => {
            return {
                name: s.name,
                ticker: s.ticker || '',
                id: s.isin,
                fund_205310: s.weights?.fund_205310 || null,
                fund_205410: s.weights?.fund_205410 || null,
                fund_206910: s.weights?.fund_206910 || null,
                fund_207610: s.weights?.fund_207610 || null,
                action: () => {
                    router.push(`/isin/${s.isin}`);
                },
            };
        });
    fuse.setCollection(results.value);
}, { immediate: true });

const searchResults = computed(() => {
    return fuse.search(input.value, {limit: 5}).map((result) => result.item);
});

// Reset selected index whenever input changes
watch(input, () => {
  selectedIndex.value = 0;
});

</script>

<template>
    <div class="page-overlay" v-show="active"></div>
    <div class="command-k" v-show="active">
        <div class="command-k-input">
            <input type="text" v-model="input" ref="inputField" />
        </div>
        <div class="command-k-results">
            <div v-for="(result, index) in searchResults" class="command-k-result"
                :class="{ '--active': index === selectedIndex }" :key="result.id" @click="result.action">
                <p class="command-k-result-title">{{ result.name }}</p>
                <p class="command-k-result-ticker">{{ result.ticker }}</p>
                <div class="fund-weights" v-if="result.fund_205310 || result.fund_205410 || result.fund_206910 || result.fund_207610">
                    <div class="fund-weight-item">
                        <p class="fund-weight-item-weight"><template v-if="result.fund_205310">{{ (result.fund_205310 * 100).toFixed(2) }}%</template><template v-else>—</template></p>
                        <p class="fund-weight-item-title">AIF</p>
                    </div>
                    <div class="fund-weight-item">
                        <p class="fund-weight-item-weight"><template v-if="result.fund_205410">{{ (result.fund_205410 * 100).toFixed(2) }}%</template><template v-else>—</template></p>
                        <p class="fund-weight-item-title">OGAW</p>
                    </div>
                    <div class="fund-weight-item">
                        <p class="fund-weight-item-weight"><template v-if="result.fund_206910">{{ (result.fund_206910 * 100).toFixed(2) }}%</template><template v-else>—</template></p>
                        <p class="fund-weight-item-title">SMC</p>
                    </div>
                    <div class="fund-weight-item">
                        <p class="fund-weight-item-weight"><template v-if="result.fund_207610">{{ (result.fund_207610 * 100).toFixed(2) }}%</template><template v-else>—</template></p>
                        <p class="fund-weight-item-title">Clean</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style>
.page-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.3);
    z-index: 50;
    backdrop-filter: blur(2px);
}

.command-k {
    position: fixed;
    top: 30%;
    left: 50%;
    width: 600px;
    transform: translate(-50%, 0);
    background: #fff;
    z-index: 51;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
    border-radius: 4px;
    overflow: hidden;
}

.command-k-input input {
    border: none;
    width: 100%;
    font: inherit;
    font-size: 32px;
    padding: 20px;
    line-height: 1;
    background: #fff;
    color: #000;
    outline: none;
}

.command-k-result {
    padding: 20px;
    font-size: 24px;
    cursor: pointer;

    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto auto;
    border-bottom: 1px solid rgba(0,0,0,0.1);
}

.command-k-result:focus,
.command-k-result:hover,
.command-k-result.--active {
    background: rgba(0, 0, 0, 0.1);
    color: #000;
}
.command-k-result-title {
    margin: 0;
    line-height: 1;
}
.command-k-result-ticker {
    grid-row-start: 1/2;
    grid-column: 1/2;
    text-transform: uppercase;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.03em;
    opacity: 0.8;
}

.fund-weights {
    margin-top: 5px;
    font-size: 14px;
    color: #666;

    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-row: 1/3;
    grid-column: 2/3;
    align-self: center;
}
.fund-weights p {
    margin: 0;
    text-align: right;
    line-height: 1;
}
.fund-weight-item-weight {
    font-size: 12px;
}
.fund-weight-item-title {
    font-size: 10px;
    opacity: 0.8;
}
</style>
